/**
 * Hook to access the minting dapp registry
 *
 * Provides reactive access to registered dapps with memoization
 * to prevent unnecessary re-renders.
 *
 * @module minting/hooks/useMintingDapp
 * @category Minting Framework
 */

import { useEffect, useMemo, useState } from 'react';
import { mintingDappRegistry } from '../registry';
import { MintingDapp } from '../types';

/**
 * Hook to get a specific minting dapp by ID.
 * Memoized to prevent re-fetches on re-renders.
 *
 * @param dappId - The ID of the minting dapp to retrieve
 * @returns The minting dapp, or undefined if not found
 *
 * @example
 * ```tsx
 * const dapp = useMintingDapp('mint-nft');
 *
 * if (dapp?.type === 'internal' && dapp.component) {
 *   return <dapp.component {...props} />;
 * }
 * ```
 */
export function useMintingDapp(dappId: string): MintingDapp | undefined {
  // The registry is filled asynchronously from the backend API after first
  // render, so subscribing is what makes a dapp appear once it lands. A bare
  // useMemo on dappId would freeze on the pre-fetch miss and render
  // "Minting Dapp Not Found" forever.
  const [, forceUpdate] = useState(0);
  useEffect(() => mintingDappRegistry.subscribe(() => forceUpdate((n) => n + 1)), []);
  return mintingDappRegistry.get(dappId);
}

/**
 * Hook to get all registered minting dapps.
 * Memoized to prevent re-fetches on re-renders.
 *
 * @returns Array of all registered minting dapps
 *
 * @example
 * ```tsx
 * const allDapps = useAllMintingDapps();
 *
 * return (
 *   <div>
 *     {allDapps.map(dapp => (
 *       <Link to={`/mint/${dapp.id}`}>{dapp.name}</Link>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useAllMintingDapps(): MintingDapp[] {
  return useMemo(() => mintingDappRegistry.getAll(), []);
}

/**
 * Hook to get dapps by type (internal or external).
 *
 * @param type - The type to filter by
 * @returns Array of dapps matching the type
 */
export function useMintingDappsByType(type: 'internal' | 'external'): MintingDapp[] {
  return useMemo(() => mintingDappRegistry.getByType(type), [type]);
}